/**
 * Best sellers service — the models the dealership wants the voice agent to
 * recommend, in the order it should offer them.
 *
 * Mirrors ai.spectraiq.backend.bestseller.controller.BestSellerController
 * (/v1/best-sellers), and feeds the `best_seller` MCP tool.
 *
 * **Curated, not computed.** The obvious implementation — aggregate the sales
 * table by model — does not work here: `sales.vehicle_id` is copied from
 * `quotes.vehicle_id`, which is nullable, and there is no API to attach a
 * vehicle after the fact. So a sale raised from a quote with no vehicle has no
 * model attribution, the aggregate silently undercounts, and nothing in the
 * answer reveals it. Beyond the data, "best seller" on a sales call is a sales
 * position rather than a statistic: a dealer may want to push what they have
 * depth or margin in. The dealership states it, the agent repeats it, and
 * nobody quotes a number they cannot defend.
 *
 * That is why `displayRank` is an order of preference and there is no unit
 * count anywhere in this payload.
 *
 * Note this controller returns DTOs directly rather than wrapping them in
 * ApiResponse, unlike most of the API — hence the tolerant unwrap below.
 */
import { api } from '../api';

/** Handles both the wrapped and bare shapes, so a backend change can't break this. */
const unwrap = (r) => r.data?.data ?? r.data;

export default {
  /** All entries, active and inactive, in rank order. */
  list: () => api.get('/v1/best-sellers').then(unwrap),

  get: (id) => api.get(`/v1/best-sellers/${id}`).then(unwrap),

  /**
   * Payload: { make, model, variant?, displayRank, talkingPoint?, vehicleId?, isActive? }
   *
   * `displayRank` must be unique among ACTIVE entries — a partial unique index
   * enforces it, so two things cannot both be rank 1. A deactivated entry keeps
   * its rank without blocking the live list.
   *
   * Leave `vehicleId` null. The tool matches make/model against live stock at
   * query time, so the answer stays current as individual cars sell; pinning a
   * specific unit goes stale the moment it does.
   *
   * `talkingPoint` is read aloud in the dealer's own words — it is what makes
   * the tool useful rather than just a list of names.
   */
  create: (payload) => api.post('/v1/best-sellers', payload).then(unwrap),

  update: (id, payload) => api.put(`/v1/best-sellers/${id}`, payload).then(unwrap),

  remove: (id) => api.delete(`/v1/best-sellers/${id}`).then(unwrap),

  /**
   * Rewrite ranks in one call: { items: [{ id, displayRank }, ...] }.
   *
   * Batched deliberately — swapping two entries one at a time would transiently
   * put both on the same rank and trip the unique index.
   */
  reorder: (items) => api.post('/v1/best-sellers/reorder', { items }).then(unwrap),
};
